// ── Cognitive Profile Engine — Athena Foco Total ──────────────────────────────

// Scoring map: each answer maps to a category (internal only)
const SCORE_MAP = [
  { A: 'PRÁTICO',      B: 'ESTRUTURADO',    C: 'VISUAL',  D: 'CONVERSACIONAL' }, // Q1
  { A: 'VISUAL',       B: 'ESTRUTURADO',    C: 'PRÁTICO', D: 'CONVERSACIONAL' }, // Q2
  { A: 'PRÁTICO',      B: 'ESTRUTURADO',    C: 'VISUAL',  D: 'CONVERSACIONAL' }, // Q3
  { A: 'PRÁTICO',      B: 'ESTRUTURADO',    C: 'VISUAL',  D: 'CONVERSACIONAL' }, // Q4
  { A: 'ESTRUTURADO',  B: 'CONVERSACIONAL', C: 'VISUAL',  D: 'PRÁTICO'        }, // Q5
  { A: 'ESTRUTURADO',  B: 'PRÁTICO',        C: 'VISUAL',  D: 'CONVERSACIONAL' }, // Q6
];

const TRAIT_DESC = {
  VISUAL:         'Aprende melhor com exemplos visuais e diagramas',
  ESTRUTURADO:    'Prefere conteúdo organizado passo a passo',
  CONVERSACIONAL: 'Processa melhor conversando e conectando ideias',
  PRÁTICO:        'Aprende com exemplos reais e prática imediata',
};

// Q2 = "o que quebra seu ritmo" → what they NEED more of
const Q2_NOTE = {
  A: 'Funciona melhor com conteúdo visual e conciso',
  B: 'Prefere informações apresentadas gradualmente',
  C: 'Tem melhor desempenho com direção e objetivos claros',
  D: 'Mantém ritmo melhor com progresso visível',
};

// Q5 = "quando aprende melhor" → learning peak condition
const Q5_PEAK = {
  A: 'Tem melhor desempenho quando vê progresso concreto',
  B: 'Aprende melhor conectando conceitos entre si',
  C: 'Processa melhor através de exemplos concretos',
  D: 'Retém mais aplicando imediatamente o que aprende',
};

// ── Core computation ──────────────────────────────────────────────────────────
export function computeCognitiveProfile(answers) {
  const scores = { VISUAL: 0, ESTRUTURADO: 0, CONVERSACIONAL: 0, PRÁTICO: 0 };
  answers.forEach((ans, i) => {
    if (ans && SCORE_MAP[i]?.[ans]) scores[SCORE_MAP[i][ans]] += 3;
  });
  const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
  const traits = sorted.map(([k]) => k);
  const summary = [
    TRAIT_DESC[traits[0]],
    TRAIT_DESC[traits[1]],
    Q2_NOTE[answers[1]],
    Q5_PEAK[answers[4]],
  ].filter(Boolean);
  return { scores, traits, summary };
}

// ── Build complete profile from base + cognitive result ───────────────────────
export function buildFullProfile(base, cogResult, answers) {
  const now = new Date().toISOString();
  return {
    ...base,
    cognitive: {
      ...cogResult,
      answers,
      createdAt: now,
      behavior: {
        sessions: 0,
        shortMessages: 0,
        longMessages: 0,
        visualRequests: 0,
        exampleRequests: 0,
        lastObserved: null,
      },
    },
    createdAt: base.createdAt || now,
    lastAccess: now,
  };
}

// ── Behavioral event tracking ─────────────────────────────────────────────────
const EVENTS = {
  SESSION_START:   b => ({ ...b, sessions: b.sessions + 1, lastObserved: new Date().toISOString() }),
  SHORT_MESSAGE:   b => ({ ...b, shortMessages: b.shortMessages + 1 }),
  LONG_MESSAGE:    b => ({ ...b, longMessages: b.longMessages + 1 }),
  VISUAL_REQUEST:  b => ({ ...b, visualRequests: b.visualRequests + 1 }),
  EXAMPLE_REQUEST: b => ({ ...b, exampleRequests: b.exampleRequests + 1 }),
};

export function trackBehavior(profile, event) {
  if (!profile?.cognitive?.behavior || !EVENTS[event]) return profile;
  return {
    ...profile,
    cognitive: {
      ...profile.cognitive,
      behavior: EVENTS[event](profile.cognitive.behavior),
    },
  };
}

// ── Detect significant behavioral drift from original profile ─────────────────
export function checkAdaptationNeeded(profile) {
  const b = profile?.cognitive?.behavior;
  if (!b || b.sessions < 4) return false;
  const msgTotal = b.shortMessages + b.longMessages;
  if (msgTotal < 12) return false;
  // Strong visual requests but non-visual primary trait
  const visualPrimary = profile.cognitive.traits[0] === 'VISUAL';
  if (!visualPrimary && b.visualRequests / Math.max(b.sessions, 1) > 2.5) return true;
  // Heavy preference for short messages in a structured/conversational profile
  const shortRatio = b.shortMessages / Math.max(msgTotal, 1);
  if (shortRatio > 0.75 && profile.cognitive.traits[0] === 'CONVERSACIONAL') return true;
  return false;
}

// ── System prompt injection (returns string to append to Foco Total prompt) ───
export function getCognitivePromptAddendum(profile) {
  if (!profile?.cognitive?.traits) return '';
  const [primary, secondary] = profile.cognitive.traits;
  const labelMap = {
    VISUAL: 'visual (prefere diagramas, exemplos visuais, conteúdo conciso)',
    ESTRUTURADO: 'estruturado (prefere passo a passo, progressão clara)',
    CONVERSACIONAL: 'conversacional (responde bem a diálogo e perguntas guiadas)',
    PRÁTICO: 'prático (aprende com exemplos reais, aplicação imediata)',
  };
  return `\n\n[PERFIL COGNITIVO DO USUARIO: ${labelMap[primary] || primary}, com tendência secundária ${labelMap[secondary] || secondary}. Adapte comunicação, profundidade e formato de resposta conforme esse perfil.]`;
}
